import React, { useCallback, useEffect, useRef, useState } from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { errorToast, toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import Popover from "@replo/design-system/components/popover/Popover";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import { ArrowUpRight, CheckCircle2, Globe, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PuffLoader } from "react-spinners";

export const PublishButton = ({
  routeType,
  publishedSiteUrl,
  showAddDomainButton,
}: {
  routeType: "funnel" | "page";
  publishedSiteUrl?: string;
  showAddDomainButton: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showTemporaryState, setShowTemporaryState] = useState<
    "success" | "error" | null
  >(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { project } = useCurrentProjectContext();

  const {
    mutate: deploy,
    isPending,
    isSuccess,
    isError,
  } = trpc.v2ShopDeployment.deploy.useMutation({
    onSuccess: () => {
      toast({
        header: `${routeType === "funnel" ? "Funnel" : "Page"} Successfully Published`,
        message: `Your updated ${routeType === "funnel" ? "pages are" : "page is"} now live.`,
        cta: "View Live",
        ctaOnClick: () => {
          window.open(publishedSiteUrl, "_blank");
        },
      });

      void trpcUtils.project.invalidate();
    },
    onError: () => {
      errorToast(
        `Error Publishing ${routeType === "funnel" ? "Funnel" : "Page"}`,
        "Please try again or contact support@replo.app.",
      );
    },
  });

  useEffect(() => {
    if (isSuccess && !isPending) {
      setShowTemporaryState("success");
      timeoutRef.current = setTimeout(() => {
        setShowTemporaryState(null);
        setIsPopoverOpen(false);
      }, 5000);
    } else if (isError && !isPending) {
      setShowTemporaryState("error");
      timeoutRef.current = setTimeout(() => {
        setShowTemporaryState(null);
        setIsPopoverOpen(false);
      }, 5000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isSuccess, isError, isPending]);

  if (!project) {
    return null;
  }

  let calculatedDeploymentState = project.deployStatus;

  if (isPending) {
    calculatedDeploymentState = "PENDING";
  }

  const siteUrlWithoutProtocol = publishedSiteUrl?.replace("https://", "");

  const shortenedSiteUrl =
    siteUrlWithoutProtocol?.slice(0, 28) +
    (siteUrlWithoutProtocol?.length && siteUrlWithoutProtocol?.length > 27
      ? "..."
      : "");

  const handlePublish = () => {
    deploy({ projectId: project.id });
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (calculatedDeploymentState === "PENDING" || showTemporaryState) {
      return;
    }
    setIsPopoverOpen(open);
  };

  const renderPopoverContent = () => {
    if (calculatedDeploymentState === "PENDING" || showTemporaryState) {
      return (
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          shouldPreventDefaultOnInteractOutside={true}
          title={`Publishing Your ${routeType === "funnel" ? "Funnel" : "Page"}`}
          description="Feel free to leave this tab."
          hideCloseButton={true}
          className="w-80"
        >
          <PublishingLoadingContent status={showTemporaryState} />
        </Popover.Content>
      );
    }

    return (
      <Popover.Content
        side="bottom"
        align="end"
        sideOffset={8}
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton={true}
        className="w-80"
        footer={
          <PublishPopoverFooter
            isPending={isPending}
            handlePublish={handlePublish}
            showAddDomainButton={showAddDomainButton}
          />
        }
        onOpenAutoFocus={(e: Event) => {
          e.preventDefault();
        }}
      >
        <PublishPopoverContent
          routeType={routeType}
          siteUrl={publishedSiteUrl}
          siteUrlWithoutProtocol={siteUrlWithoutProtocol}
          shortenedSiteUrl={shortenedSiteUrl}
        />
      </Popover.Content>
    );
  };

  return (
    <Popover.Root isOpen={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
      <Popover.Trigger asChild>
        <Button
          variant="primary"
          size="base"
          onClick={() => setIsPopoverOpen(true)}
        >
          {routeType === "funnel" ? "Publish Funnel" : "Publish Page"}
        </Button>
      </Popover.Trigger>

      {renderPopoverContent()}
    </Popover.Root>
  );
};

const PublishingLoadingContent = ({
  status,
}: {
  status: "success" | "error" | null;
}) => {
  const simulatedProgress = useSimulatedProgress(
    status === null, // isActive
    75000, // 75 seconds duration
    2, // 2 updates per second for smoother progress
  );

  // Use simulated progress when still loading, 100% when successful
  const displayProgress =
    status === "success" || status === "error" ? 100 : simulatedProgress;
  const displayPercentage = Math.round(displayProgress);

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <img
          src="/images/shops/Globe.png"
          alt="Globe"
          className="absolute top-1/2 left-1/2 w-16 h-16 -translate-x-[56%] -translate-y-[56%] z-10"
        />
        <PuffLoader color="#2563EB" size={120} speedMultiplier={0.75} />
      </div>

      <div className="w-full flex gap-3 items-center">
        <Progress
          value={displayProgress}
          className="h-2 bg-gray-200 [&>div]:bg-black [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out" // eslint-disable-line replo/no-tailwind-literal-color
        />
        {!status && (
          <span className="text-header-h5 font-semibold text-base">
            {displayPercentage}%
          </span>
        )}
        {status === "success" && <CheckCircle2 className="w-6 h-6" />}
        {status === "error" && <X className="w-6 h-6" />}
      </div>
    </div>
  );
};

const PublishPopoverContent = ({
  routeType,
  siteUrl,
  siteUrlWithoutProtocol,
  shortenedSiteUrl,
}: {
  routeType: "funnel" | "page";
  siteUrl?: string;
  siteUrlWithoutProtocol?: string;
  shortenedSiteUrl?: string;
}) => {
  return (
    <div className="flex flex-col gap-4 p-3 border-border border-0.5 rounded-lg bg-sheet">
      {siteUrl ? (
        <Button
          variant="link"
          size="base"
          onClick={() => {
            window.open(siteUrl, "_blank");
          }}
          startEnhancer={<Globe className="w-4 h-4 mr-1.5" />}
          tooltipText={siteUrlWithoutProtocol}
          UNSAFE_contentClassName="text-default"
          titleAlignment="start"
        >
          <div className="flex items-center">
            {shortenedSiteUrl}
            <ArrowUpRight className="w-4 h-4 ml-1.5" />
          </div>
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="text-sm text-muted">
            Publish your {routeType} to see your url!
          </div>
        </div>
      )}
    </div>
  );
};

const PublishPopoverFooter = ({
  isPending,
  handlePublish,
  showAddDomainButton,
}: {
  isPending: boolean;
  handlePublish: () => void;
  showAddDomainButton: boolean;
}) => {
  const navigate = useNavigate();
  const { project } = useCurrentProjectContext();

  const handleAddDomain = () => {
    if (project?.id) {
      navigate(`/shops/${project.id}/settings/shop/connections`);
    }
  };

  return (
    <div className="flex gap-3">
      {showAddDomainButton && (
        <Button
          variant="secondary"
          size="base"
          onClick={handleAddDomain}
          layoutClassName="flex-1"
        >
          Add Domain
        </Button>
      )}
      <Button
        variant="primary"
        size="base"
        onClick={handlePublish}
        isLoading={isPending}
        layoutClassName="flex-1"
      >
        Publish
      </Button>
    </div>
  );
};

// Simple easing function - starts fast, slows down at the end
const easeOut = (t: number): number => {
  return 1 - Math.pow(1 - t, 2);
};

const useSimulatedProgress = (
  isActive: boolean,
  targetDuration: number = 75000, // 75 seconds
  updatesPerSecond: number = 4, // How often to update (1 = once per second, 2 = twice per second, 0.5 = every 2 seconds)
) => {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousProgressRef = useRef(0);

  // Convert updates per second to milliseconds
  const updateInterval = 1000 / updatesPerSecond;

  const updateProgress = useCallback(() => {
    if (!startTimeRef.current || !isActive) {
      return;
    }

    const elapsed = Date.now() - startTimeRef.current;
    const normalizedTime = Math.min(elapsed / targetDuration, 1);

    // Simple progression - fast at first, slower at the end
    const easedProgress = easeOut(normalizedTime);

    // Add small random bump (0-2%) to make it feel less predictable
    const randomBump = Math.random() * 0.02;
    const calculatedProgress = Math.min((easedProgress + randomBump) * 98, 98); // Cap at 98%

    // Ensure progress never decreases
    const newProgress = Math.max(
      calculatedProgress,
      previousProgressRef.current,
    );

    previousProgressRef.current = newProgress;
    setProgress(newProgress);

    // Stop the interval when we reach the end
    if (normalizedTime >= 1 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isActive, targetDuration]);

  useEffect(() => {
    if (isActive && !startTimeRef.current) {
      startTimeRef.current = Date.now();
      previousProgressRef.current = 0;
      setProgress(0);

      // Start updates after a brief delay to show 0% first
      setTimeout(() => {
        if (isActive) {
          intervalRef.current = setInterval(updateProgress, updateInterval);
        }
      }, 100);
    } else if (!isActive) {
      startTimeRef.current = null;
      previousProgressRef.current = 0;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, updateProgress, updateInterval]);

  return progress;
};
